<template>
  <div
    v-if="createLayoutStore.createRenderType === 'pc'"
    class="store-detail height-all"
  >
    <div class="store-detail-main flex-column height-all">
      <div class="back pointer" v-if="!isFromShare" @click="goBack">
        <img src="@/assets/img/store/back.png" alt="" />
        <span>{{ $t("store.back") }}</span>
      </div>
      <div class="store-detail-songcard" v-if="!renderData.notFound">
        <SongCard
          v-if="info?.mp3_url"
          :isGenerating="renderData.isGenerating"
          class="detail"
          type="detail"
          :data="info"
          @togglePlay="
            (isPlay) => {
              togglePlay(info, isPlay)
            }
          "
          @addCartSuccess="(buyType) => addCartSuccess(info, buyType)"
          @addCartFail="addCartFail(info)"
          @changeFavorite="(state) => changeFavorite(info, state)"
          @bugNoexclusive="bugNoexclusive(info)"
          @progress="(p) => progressChange(p)"
          @share="handleShare"
          @handleSimilar="handleSimilar"
          @deleteSong="doSongHasDeleted"
        ></SongCard>
        <span v-if="$$language() === 'cn'" class="tip">当前内容由AI生成</span>
      </div>
      <div
        ref="lyricBlockRef"
        v-if="!renderData.notFound"
        @scroll="handleScroll"
        class="lyrics height100-scroll"
      >
        <template v-for="(item, index) in formattedSegments" :key="index">
          <div class="line" :class="`${currentLine === index && 'active'}`">
            {{ item.text }}
          </div>
        </template>
      </div>

      <div class="store-detail-not-found flex1 height-0">
        <NotFound
          v-if="renderData.notFound"
          renderType="songNotFound"
        ></NotFound>
      </div>
    </div>
  </div>
  <MobileDetail
    v-else
    :isGenerating="renderData.isGenerating"
    @handleSimilar="handleSimilar"
    :notFound="renderData.notFound"
    @deleteSong="doSongHasDeleted"
  ></MobileDetail>
  <CreateSimilar
    ref="refCreateSimilar"
    source="detail"
    @songHasDeleted="doSongHasDeleted"
  ></CreateSimilar>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  getCurrentInstance,
  watchEffect,
  reactive,
} from "vue"
import { useRouter, useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import message from "@/components/functionCallComponent/message/message.js"
import { $$t, $$language } from "@/i18n/i18n.js"
import useShopCartStore from "@/store/shopCartStore.js"
import SongCard from "./components/songCardNew.vue"
import useGlobalPlayDataStore from "@/store/globalPlayDataStore.js"
import useDiscoverDataStore from "@/store/discover/discoverDataStore.js"
import useStoreDetailData from "@/store/storeDetail/storeDetailDataStore.js"
import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import MobileDetail from "../main/mobileStoreDetailNew.vue"
import useUserDataStore from "@/store/userDataStore"
import CreateSimilar from "@/page/create/createSimillar/createSimilar.vue"
import NotFound from "@/page/notfound.vue"

import tools from "@/utils/tools.js"

const discoverDataStore = useDiscoverDataStore()
const createLayoutStore = useCreateLayoutStore()
const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties
const globalPlayDataStore = useGlobalPlayDataStore()
const storeDetailDataStore = useStoreDetailData()
const { info } = storeToRefs(storeDetailDataStore)
const shopCartStore = useShopCartStore()
const userDataStore = useUserDataStore()

const Router = useRouter()
const route = useRoute()
const isFromShare = ref(false)
const progress = ref(0)
const lyricBlockRef = ref()
const currentLine = ref(0)
const lastScrollTime = ref(0)
const refCreateSimilar = ref(null)

const renderData = reactive({
  notFound: false,
  isGenerating: false,
  curPlayData: globalPlayDataStore.curPlayData,
})

const progressChange = (p) => {
  progress.value = p
  scrollToTheLine()
}

const formattedSegments = computed(() => {
  return formatSegments(info.value?.lyrics)
})

let scrollTimer = null
const handleScroll = () => {
  lastScrollTime.value = Date.now()
  if (scrollTimer) {
    clearTimeout(scrollTimer)
  }
  scrollTimer = setTimeout(() => {
    console.log("scroll end")
    scrollToTheLine()
  }, 3020)
}

const isSelf = computed(() => {
  const isUserDataUser = userDataStore.userInfo?.user?.user_id
  const isDataUser = info.value?.user?.user_id
  return (isUserDataUser || false) && isUserDataUser === isDataUser
})

const handleSimilar = (data) => {
  refCreateSimilar.value?.initSimilar(data)
}

const getNextLine = () => {
  if (!info.value?.duration_milliseconds) return 0
  const currentTime = (progress.value * info.value?.duration_milliseconds) / 100
  const nextLine = (formattedSegments.value ?? []).findIndex((item, index) => {
    if (
      item.start !== undefined &&
      item.end !== undefined &&
      item.start != item.end &&
      currentTime >= item.start &&
      currentTime <= item.end &&
      item.type == "lyric"
    ) {
      return true
    }
    return false
  })
  return nextLine
}

const scrollToTheLine = () => {
  const lyricBlock = lyricBlockRef.value
  if (!lyricBlock) return
  const nextLine = getNextLine()
  currentLine.value = nextLine
  const line = lyricBlock.querySelectorAll(".line")
  if (line[currentLine.value]) {
    if (Date.now() - lastScrollTime.value < 3000) {
      // console.log("scroll too fast", Date.now() - lastScrollTime.value);
      return
    }
    line[currentLine.value].scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }
  // TODO: 模拟每5%滚动一行，然后已5%为单位进行歌词进度滚动
  // currentProgress.value = ((props.progress % 5) / 5) * 100;
  // gsap.to(lyricBlock.querySelector(".line-mask"), {
  //   clipPath: `inset(0 ${100 - currentProgress.value}% 0 0)`,
  //   duration: 0.3,
  // });
}

watchEffect(() => {
  scrollToTheLine()
})

const formatSegments = (text) => {
  return text?.reduce((res, itemp) => {
    if (itemp.user_input_tag) {
      res.push({
        id: tools.getUuid(),
        type: "lyric_seg",
        seg_type: itemp.seg_type,
        text: itemp.user_input_tag,
        start: itemp.start,
        end: itemp.end,
      })
    }
    return (itemp.rows ?? []).reduce((res, item) => {
      res.push({
        id: tools.getUuid(),
        type: "lyric",
        seg_type: itemp.seg_type,
        text: item.text,
        start: item.start,
        end: item.end,
      })
      return res
    }, res)
  }, [])
}

const goBack = () => {
  Router.back()
}

const doSongHasDeleted = () => {
  // getDetail();
}
const getDetail = () => {
  new Promise((resolve, reject) => {
    const {
      query: { share_key, id },
      params: { song_id },
    } = route
    const query = isFromShare.value
      ? { share_key: share_key || song_id }
      : { song_id: id || song_id }
    storeDetailDataStore.getInfo(query).then((res) => {
      if (res.code == 200) {
        renderData.notFound = false

        const isReturnSelf = () => {
          const isUserDataUser = userDataStore.userInfo?.user?.user_id
          const isDataUser = res?.data?.user?.user_id
          return (isUserDataUser || false) && isUserDataUser === isDataUser
        }
        if (isReturnSelf()) {
          globalProperties.logViewEvent("song_detail_page_video", {
            type: "active_state",
          })
        } else {
          if (res?.data?.song?.video?.video_url) {
            globalProperties.logViewEvent("song_detail_page_video", {
              type: "passive_state",
            })
          }
        }
        const { machine_state } = res?.data?.song
        if (machine_state == 4 && isFromShare.value) {
          message.warning({
            maskClosable: true,
            content: $$t("store.songDeleted"),
            position: "top",
          })
          Router.replace({
            name: "create",
          })
        } else {
          renderData.isGenerating =
            !/\.[0-9A-Za-z]{1,}$/.test(res?.data?.song.mp3_url ?? "") &&
            res?.data?.song.stream_key
          storeDetailDataStore.updateDetail(res?.data?.song, res?.data)
          if (route.name == "songDetail" || route.name == "songDetailOld") {
            const title = res?.data?.song?.title ?? "Mureka"
            const lyrics = formatSegments(res?.data?.song?.lyrics ?? [])
            console.log("title_lyrics", title, lyrics)
            // document.title = title;
            const lyricsText = lyrics.reduce((res, item) => {
              if (item.type == "lyric") {
                res += item.text + " "
              }
              return res
            }, "")
            document.getElementById("meta_description").attributes[
              "content"
            ].value = lyricsText
          }
        }
        resolve()
        // if (isFromShare.value) {
        //   globalProperties.logViewEvent("song_share_page", {
        //     id: res?.data?.song?.song_id,
        //   });
        // } else {
        //   globalProperties.logViewEvent("song_detail_page", {
        //     id: res?.data?.song?.song_id,
        //     source: route.query.source,
        //   });
        // }
      } else if (res.code == 6332) {
        message.info({
          position: "top",
          content: $$t("create.the_song_does_not_exist"),
        })
        const isFromShareC =
          route.query?.is_from_share == 1 ||
          !/^[0-9]{1,}$/.test(route.params?.song_id ?? "")
        const queryC = isFromShareC
          ? { share_key: route.query?.share_key || route.params?.song_id }
          : { song_id: route.query?.id || route.params?.song_id }
        if (
          query.share_key == queryC.share_key ||
          query.song_id == queryC.song_id
        ) {
          renderData.notFound = true
        } else {
          renderData.notFound = false
        }
        if (
          (globalPlayDataStore.curPlayData?.song_id ==
            storeDetailDataStore.info?.song_id ||
            globalPlayDataStore.curPlayData?.song_id == song_id ||
            (share_key &&
              share_key == globalPlayDataStore.curPlayData?.share_key)) &&
          globalPlayDataStore.curPlayData?.song_id
        ) {
          globalPlayDataStore.removePlayDataItem(
            globalPlayDataStore.curPlayData
          )
        }
      } else if (res.code == 400) {
        message.info({
          position: "top",
          content: $$t("create.song_does_not_exist"),
        })
        const isFromShareC =
          route.query?.is_from_share == 1 ||
          !/^[0-9]{1,}$/.test(route.params?.song_id ?? "")
        const queryC = isFromShareC
          ? { share_key: route.query?.share_key || route.params?.song_id }
          : { song_id: route.query?.id || route.params?.song_id }
        if (
          query.share_key == queryC.share_key ||
          query.song_id == queryC.song_id
        ) {
          renderData.notFound = true
        } else {
          renderData.notFound = false
        }
      } else {
        renderData.notFound = false
      }
    })
  })
}

const changeFavorite = (data, state) => {
  discoverDataStore.updateGlobalPlayFavorite(data.song_id, state)
  shopCartStore.updateShopLike(data.song_id, state)
  storeDetailDataStore.updateDetail({
    ...info.value,
    favorite_state: state,
    is_liked: state === 1 ? true : false,
  })
  globalPlayDataStore.updatePlayDataItem({
    ...info.value,
    favorite_state: state,
    is_liked: state === 1 ? true : false,
  })
  // 客态收藏歌曲
  if (!isSelf.value && state === 1) {
    globalProperties.logClickEvent("song_like", {
      entity: "song_like",
      content_id: info.value?.song_id,
    })
  }
  console.log("changeFavorite", data, state)
}

const handleShare = () => {
  globalProperties.logClickEvent("song_copy_link", {
    is_self: isSelf.value ? 1 : 2,
    entity: "song_copy_link",
    content_id: info.value?.song_id,
  })
}

const addCartSuccess = (data, buyType) => {
  globalProperties.logClickEvent("song_license_type_add_to_cart", {
    content_id: data.song_id,
    source: isFromShare.value ? "song_share_page" : "song_detail_page",
    type: buyType == 1 ? "Non-exclusive" : "Exclusive",
  })
  storeDetailDataStore.updateDetail({
    ...info.value,
    cart_state: 1,
  })
  shopCartStore.updateShopCart(data.song_id, 1)
  globalPlayDataStore.updatePlayDataItem({
    ...info.value,
    cart_state: 1,
  })
}

const addCartFail = (data) => {
  info.value = {
    ...info.value,
    sold_state: 4,
  }
  shopCartStore.deleteShopItem(data.song_id)
  globalPlayDataStore.updatePlayDataItem({
    ...info.value,
    sold_state: 4,
    cart_state: 3,
  })
}

const bugNoexclusive = (data) => {
  shopCartStore.updateShopOrderState(data.song_id, 1)
  shopCartStore.updateShopCart(data.song_id, 3)
  storeDetailDataStore.updateDetail({
    ...info.value,
    order_state: 1,
  })
  globalPlayDataStore.updatePlayDataItem({
    ...data,
    order_state: 1,
    cart_state: 3,
  })
}

const togglePlay = (item, isPlay) => {
  globalProperties.logClickEvent("song_play", {
    content_id: item.song_id,
    source: isFromShare.value ? "song_share_page" : "song_detail_page",
  })
  globalPlayDataStore.switchCurPlayData({
    ...item,
    mp3_url: item.song_url,
    favorite_state: item.is_liked ? 1 : 2,
  })
  globalProperties.logClickEvent("song_detail_page_play", {
    is_self: isSelf.value ? 1 : 2,
    entity: "song_detail_page_play",
    content_id: `${info.value?.song_id}`,
  })
}

const init = (isInit) => {
  const {
    query: { share_key, id, is_from_share },
    params: { song_id },
  } = route
  isFromShare.value = is_from_share == 1 || !/^[0-9]{1,}$/.test(song_id ?? "")

  storeDetailDataStore.setCurDetailIdOrKey({
    share_key: isFromShare.value ? share_key || song_id : null,
    song_id: isFromShare.value
      ? null
      : id || song_id
      ? Number(id || song_id)
      : null,
  })
  getDetail()
  globalProperties.logViewEvent("per_song_detail_page", {
    content_id: song_id?.toString() ?? share_key?.toString(),
  })
}

const doRefreshDetail = (oldCurPlayData, newCurPlayData) => {
  if (
    oldCurPlayData?.song_id != newCurPlayData?.song_id &&
    info.value?.song_id != oldCurPlayData?.song_id
  ) {
    setTimeout(() => {
      init()
    }, 50)
  }
}

watch(
  () => {
    return route?.params
  },
  () => {
    init()
  }
)

watch(
  () => {
    return globalPlayDataStore.curPlayData
  },
  () => {
    doRefreshDetail(renderData.curPlayData, globalPlayDataStore.curPlayData)

    renderData.curPlayData = globalPlayDataStore.curPlayData
    if (
      ((route.name == "songDetail" &&
        route.params?.song_id != globalPlayDataStore.curPlayData?.song_id) ||
        (route.name == "songDetailOld" &&
          route.params?.song_id != globalPlayDataStore.curPlayData?.song_id)) &&
      globalPlayDataStore.curPlayData?.song_id
    ) {
      Router.replace({
        name: "songDetail",
        params: {
          source: "switch",
          song_id: globalPlayDataStore.curPlayData?.song_id,
        },
        query: {
          ...(route.params?.from || route.query?.from
            ? { from: route.params?.from || route.query?.from }
            : {}),
        },
      })
    } else {
      // router.push({
      //   name: "songDetail",
      //   query: {
      //     source: "switch",
      //   },
      //   params: {
      //     song_id: curPlayData?.song_id,
      //   }
      // });
    }
  }
)

watch(
  () => {
    return info.value
  },
  () => {
    renderData.isGenerating =
      !/\.[0-9A-Za-z]{1,}$/.test(info.value?.mp3_url ?? "") &&
      info.value?.stream_key

    if (!info.value?.song_id && !globalPlayDataStore.curPlayData?.song_id) {
      renderData.notFound = true
    }
  }
)

onMounted(async () => {
  init(true)
})

const isLogged = ref(false)

watchEffect(() => {
  if (info.value?.song_id && !isLogged.value) {
    isLogged.value = true
    globalProperties.logViewEvent("song_detail_page", {
      entity: "song_detail_page",
      content_id: `${info.value?.song_id}`,
    })
  }
})

onUnmounted(() => {
  isLogged.value = false
  storeDetailDataStore.updateDetail({})
})
</script>

<style lang="scss">
.store-detail {
  font-family: HarmonyOS Sans SC;
  color: #fff;
  padding: 0 20px;
  .store-detail-songcard {
    height: 268px;
    margin-top: 20px;
    position: relative;
    .tip {
      position: absolute;
      right: 35px;
      bottom: 12px;
      font-size: 10px;
      opacity: 0.4;
      font-weight: 400;
    }
  }
  .store-detail-main {
    .back {
      width: max-content;
      height: 40px;
      display: flex;
      border-radius: 60px;
      background: rgba(255, 255, 255, 0.1);
      align-items: center;
      margin-top: 20px;
      padding: 8px 20px 8px 0;
      img {
        width: 24px;
        height: 24px;
        margin-left: 8px;
      }
      span {
        margin-left: 8px;
        font-size: 18px;
        font-weight: 500;
        line-height: 22px;
      }
    }

    .store-detail-not-found {
      .not-found {
        height: 100%;
      }
    }
    .detail {
      // background: linear-gradient(
      //   100deg,
      //   rgba(148, 173, 255, 0.1) 23%,
      //   rgba(61, 213, 255, 0.1) 89%
      // );
      padding: 0px;
      .title-box {
        margin: 0 0 0 32px;
        height: 268px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        .title-wrapper {
          align-items: end;
        }
        .title {
          font-size: 36px;
          font-weight: 500;
          line-height: 1.2;
          margin-left: -2px;
        }
        .publish-icon {
          margin-bottom: 9px;
        }
        .tag {
          margin-top: 12px;
          display: -webkit-box;
          -webkit-line-clamp: 2; /* Limit to 2 lines */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .nickname {
          font-size: 14px;
          font-weight: normal;
          line-height: 16px;
          margin-top: 12px;
        }
        .gen-date {
          flex: 1;
        }
      }

      .wave-box {
        margin-top: -40px;
      }

      .use-box {
        flex: none;
        margin-top: 0px;
        align-items: center;
        transform: translateY(-3px);
      }
    }
    .lyrics {
      user-select: text;
      padding: 12px 0 12px 0px;
      margin-top: 20px;

      line-height: 38px;
      scroll-padding-top: 114px;

      .line {
        font-size: 18px;
        font-weight: 400;
        color: #fff;
        transition: all 0.5s ease;
      }
      .line.active {
        font-weight: 700;
        font-size: 24px;
        color: #94adff;
      }
    }
  }
}

.main-page-bottom-player-visible--true {
  &.main-page--pc {
    .lyrics {
      padding-bottom: 78px;
    }
  }
}
</style>
